import { useState, useEffect, useCallback, useMemo } from "react";
import { authFetch } from "@/lib/authFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// FIN-4 — useFinanceInvestimentos.
//
// Agregador de dados pra Investimentos tab. 4 fetches em paralelo:
//   /api/investments
//   /api/investments/summary
//   /api/investments/by-class
//   /api/investments/concentration-warning
//
// Detecção Open Finance: qualquer investimento com pluggy_position_id
// liga modo OF ON. Default OFF (manual).
//
// CRUD: createInvestment / updateInvestment / deleteInvestment +
// patch otimista local antes do refetch.
//
// Filtro client-side por classe (pills) — frontend filtra a lista
// completa sem refetch.

const VALID_CLASSES = new Set(["Renda Fixa", "Renda Variável", "Cripto", "Outros"]);

async function _safeJson(url) {
  try {
    const r = await authFetch(url);
    if (!r.ok) return { error: `HTTP ${r.status}`, data: null };
    return { error: null, data: await r.json() };
  } catch (e) {
    return { error: String(e.message || e), data: null };
  }
}

export default function useFinanceInvestimentos() {
  const [investments, setInvestments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [byClass, setByClass] = useState(null);
  const [concentrationWarning, setConcentrationWarning] = useState(null);
  const [classFilter, setClassFilter] = useState("Todos");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const [a, s, c, w] = await Promise.all([
      _safeJson(`${API_URL}/api/investments`),
      _safeJson(`${API_URL}/api/investments/summary`),
      _safeJson(`${API_URL}/api/investments/by-class`),
      _safeJson(`${API_URL}/api/investments/concentration-warning`),
    ]);
    if (a.data) setInvestments(Array.isArray(a.data?.investments) ? a.data.investments : []);
    if (s.data) setSummary(s.data);
    if (c.data) setByClass(c.data);
    if (w.data) setConcentrationWarning(w.data);
    const errs = [a.error, s.error, c.error, w.error].filter(Boolean);
    if (errs.length) setError(errs[0]);
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // OF ON: any investment com pluggy_position_id setado
  const isOpenFinance = useMemo(
    () => investments.some(i => Boolean(i.pluggy_position_id)),
    [investments],
  );

  const filteredInvestments = useMemo(() => {
    if (!classFilter || classFilter === "Todos") return investments;
    if (!VALID_CLASSES.has(classFilter)) return investments;
    return investments.filter(i => i.asset_class === classFilter);
  }, [investments, classFilter]);

  // Lista de instituições conhecidas pra autocomplete
  const knownInstitutions = useMemo(() => {
    const s = new Set();
    investments.forEach(i => { if (i.institution) s.add(i.institution); });
    return Array.from(s).sort();
  }, [investments]);

  // CRUD
  const createInvestment = useCallback(async (payload) => {
    const r = await authFetch(`${API_URL}/api/investments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    await fetchAll();
    return fresh;
  }, [fetchAll]);

  const updateInvestment = useCallback(async (id, patch) => {
    const r = await authFetch(`${API_URL}/api/investments/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const fresh = await r.json();
    // Optimistic local — refetch agregados em background
    setInvestments(prev => prev.map(i => (i.id === id ? { ...i, ...fresh } : i)));
    fetchAll();
    return fresh;
  }, [fetchAll]);

  const deleteInvestment = useCallback(async (id) => {
    const r = await authFetch(`${API_URL}/api/investments/${id}`, { method: "DELETE" });
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    await fetchAll();
  }, [fetchAll]);

  const exportCsv = useCallback(async () => {
    const r = await authFetch(`${API_URL}/api/investments/export?format=csv`);
    if (!r.ok) {
      const detail = await r.json().catch(() => ({}));
      throw new Error(detail?.detail || `HTTP ${r.status}`);
    }
    const blob = await r.blob();
    const cd = r.headers.get("Content-Disposition") || "";
    const m = /filename="([^"]+)"/.exec(cd);
    const filename = m ? m[1] : `investimentos_${new Date().toISOString().slice(0, 10)}.csv`;
    const a = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    return { filename, size: blob.size };
  }, []);

  return {
    investments,
    filteredInvestments,
    summary,
    byClass,
    concentrationWarning,
    isOpenFinance,
    knownInstitutions,
    classFilter,
    setClassFilter,
    isLoading,
    error,
    refetch: fetchAll,
    createInvestment,
    updateInvestment,
    deleteInvestment,
    exportCsv,
  };
}
